import { countPending } from '../lib/db';
import { reconnectRealtime } from '../hooks/useRealtime';
import { syncOutbox, type SyncOutcome } from './outboxSync';

/**
 * Watches the connection and drains the outbox when it returns (CLAUDE.md Rule 4).
 *
 * This is the only place that listens for `online`. Two listeners would each kick off
 * a sync on the same event, and two concurrent drains would race over the same rows.
 */

export type NetworkStatus =
  | { kind: 'offline' }
  | { kind: 'syncing'; pending: number }
  | { kind: 'synced'; sent: number }
  | { kind: 'sync-failed'; failed: number };

type StatusListener = (status: NetworkStatus) => void;

const listeners = new Set<StatusListener>();
let inFlight: Promise<SyncOutcome> | null = null;
let started = false;

function emit(status: NetworkStatus): void {
  for (const listener of listeners) listener(status);
}

/** Subscribe to connection and sync events. Returns an unsubscribe function. */
export function onNetworkStatus(listener: StatusListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Drains the outbox, collapsing concurrent calls onto one run.
 *
 * `online` fires in bursts on flaky hotel wifi, and each burst must not start its own
 * drain of the same queue.
 */
export async function triggerSync(): Promise<SyncOutcome> {
  if (inFlight) return inFlight;

  const pending = await countPending();
  if (pending > 0) emit({ kind: 'syncing', pending });

  inFlight = syncOutbox()
    .then((outcome) => {
      if (outcome.sent > 0) emit({ kind: 'synced', sent: outcome.sent });
      if (outcome.failed > 0) emit({ kind: 'sync-failed', failed: outcome.failed });
      return outcome;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

/**
 * Installs the listeners. Safe to call twice — a second call is ignored rather than
 * doubling every handler.
 */
export function startNetworkMonitor(): void {
  if (started) return;
  started = true;

  const handleOnline = () => {
    // The socket reconnects on its own, but nudging it means the live feed is back
    // before the user notices, rather than after the next backoff tick.
    reconnectRealtime();
    void triggerSync();
  };

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', () => emit({ kind: 'offline' }));

  // The phone may have regained signal while the app was closed.
  if (navigator.onLine) void triggerSync();

  // Manual trigger for browser-console debugging and e2e tests.
  (window as unknown as Record<string, unknown>).__TRIGGER_OUTBOX_SYNC__ = triggerSync;
}
